import {
  LuxButton,
  LuxTypography,
  UserIcon,
  useTheme,
} from '@gemini/fe-ds-core';
import {
  ColumnWrapper,
  DetailSection,
  InsPtCtDetailsWrapper,
  ItemWrapper,
  PatientPill,
  SubSectionTitle,
} from '../BookingDetails.styles';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { formatPhone, formatPatientAddress } from '../utils';
import { FormConfigurationDTO, TruePatient } from '@utils/interfaces';
import { mergeCustomFields } from '@utils/functions';
import { Patient } from '@store/patientStore';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useGetTextColor from '@hooks/useGetTextColor';
import dayjs from 'dayjs';

interface PatientDetailsSectionProps {
  patients: Array<TruePatient>;
  currentPatient: number;
  onEditClick: (url: string, step: StepsStep) => void;
  formConfigsFromProps?: FormConfigurationDTO;
  isFullRecap?: boolean;
  hideEditButton?: boolean;
  isLast?: boolean;
  isTyp?: boolean;
}

export const PatientDetailsSection = ({
  patients,
  currentPatient,
  isFullRecap,
  onEditClick,
  formConfigsFromProps,
  hideEditButton = false,
  isLast = false,
  isTyp = false,
}: PatientDetailsSectionProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const { getTextColor } = useGetTextColor();
  const theme = useTheme();

  const setShowAddPatientFrame = useOnlineBookingStore(
    state => state.setShowAddPatientFrame,
  );
  const brandName = useOnlineBookingStore(state => state.brandName);
  const onlyTruePatients = useMemo(() => patients.filter(p => p), [patients]);

  const isPatientSelected = useCallback(
    (key: number) => currentPatient === key,
    [currentPatient],
  );

  const getPatientName = useCallback(
    (index: number) => patients[index]?.content,
    [patients],
  );

  const onEditPatientDetailsClick = () => {
    setShowAddPatientFrame(false);
    onEditClick(Urls.PATIENT_DETAILS, StepsStep.PATIENT_DETAILS);
  };

  const formConfigs = useOnlineBookingStore(state => state.formConfigs);

  const customFieldsPatient = useCallback(
    (patient: Patient) => {
      if (
        formConfigs?.patientDetails.customFieldsPatient?.children &&
        patient.patientDetails.storeCustomFieldsPatient
      ) {
        return mergeCustomFields(
          patient.patientDetails.storeCustomFieldsPatient,
          formConfigs.patientDetails.customFieldsPatient.children,
        );
      } else if (
        formConfigsFromProps?.patientDetails.customFieldsPatient?.children &&
        patient.patientDetails.storeCustomFieldsPatient
      ) {
        return mergeCustomFields(
          patient.patientDetails.storeCustomFieldsPatient,
          formConfigsFromProps.patientDetails.customFieldsPatient.children,
        );
      } else return undefined;
    },
    [formConfigs, formConfigsFromProps],
  );

  return (
    <DetailSection isLast={isLast}>
      <SubSectionTitle>
        <LuxTypography variant="textMedium">
          {t('detailsSection.ptDetails')}
        </LuxTypography>
        {!hideEditButton && (
          <LuxButton
            variant="ghost"
            iconStart="EditIcon"
            size="small"
            onClick={() => onEditPatientDetailsClick()}
            style={{ textDecoration: brandName && 'underline' }}
          >
            <LuxTypography
              variant="textMedium"
              fontWeight={brandName && '700!important'}
            >
              {t('generalCta.edit')}
            </LuxTypography>
          </LuxButton>
        )}
      </SubSectionTitle>
      <InsPtCtDetailsWrapper halfWidth={isFullRecap}>
        {patients.map((patient, index) => {
          if (patient.patient?.patientDetails?.firstName) {
            return (
              <ItemWrapper key={index} direction={'vertical'}>
                {onlyTruePatients.length > 1 && (
                  <PatientPill
                    customTextColor={getTextColor(
                      !isTyp && isPatientSelected(patient.tabKey)
                        ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                        : theme.palette.LUX.obTokens.bgColorBaseDefault,
                    )}
                    customColor={
                      !isTyp && isPatientSelected(patient.tabKey)
                        ? theme.palette.LUX.obTokens.bgColorBrandPrimary
                        : theme.palette.LUX.obTokens.bgColorBaseDefault
                    }
                    customBorderColor={
                      theme.palette.LUX.obTokens.bordersColorNeutralSecondary
                    }
                  >
                    <UserIcon
                      color={
                        isPatientSelected(patient.tabKey) && !isTyp
                          ? 'white'
                          : 'black'
                      }
                    />
                    <LuxTypography
                      variant="textXs"
                      color={
                        isPatientSelected(patient.tabKey) && !isTyp
                          ? 'white'
                          : 'black'
                      }
                    >
                      {getPatientName(index)}
                    </LuxTypography>
                  </PatientPill>
                )}
                {/* <PtDetailsWrapper> */}
                <ColumnWrapper>
                  <ItemWrapper>
                    <LuxTypography variant="textMdRegular">
                      {t('fieldsLabel.name')}
                    </LuxTypography>
                    <LuxTypography variant="textMedium">
                      {`${patient.patient.patientDetails.firstName} ${patient.patient.patientDetails.lastName}`}
                    </LuxTypography>
                  </ItemWrapper>
                  {patient.patient.patientDetails.email && (
                    <ItemWrapper>
                      <LuxTypography variant="textMdRegular">
                        {t('fieldsLabel.emailShort')}
                      </LuxTypography>
                      <LuxTypography variant="textMedium">
                        {patient.patient.patientDetails.email}
                      </LuxTypography>
                    </ItemWrapper>
                  )}
                  {formatPatientAddress(patient.patient.patientDetails) && (
                    <ItemWrapper>
                      <LuxTypography variant="textMdRegular">
                        {t('fieldsLabel.address')}
                      </LuxTypography>
                      <LuxTypography variant="textMedium">
                        {formatPatientAddress(patient.patient.patientDetails)}
                      </LuxTypography>
                    </ItemWrapper>
                  )}
                  {/* </ColumnWrapper>
                  <ColumnWrapper> */}
                  <ItemWrapper>
                    <LuxTypography variant="textMdRegular">
                      {t('fieldsLabel.dob')}
                    </LuxTypography>
                    <LuxTypography variant="textMedium">
                      {dayjs(patient.patient.patientDetails.dateOfBirth).format(
                        'MM/DD/YYYY',
                      )}
                    </LuxTypography>
                  </ItemWrapper>
                  {formatPhone(patient.patient.patientDetails.phone, true) ? (
                    <ItemWrapper>
                      <LuxTypography variant="textMdRegular">
                        {t('fieldsLabel.phone')}
                      </LuxTypography>
                      <LuxTypography variant="textMedium">
                        {formatPhone(
                          patient.patient.patientDetails.phone,
                          true,
                        )}
                      </LuxTypography>
                    </ItemWrapper>
                  ) : (
                    <></>
                  )}

                  {customFieldsPatient(patient.patient) &&
                    customFieldsPatient(patient.patient)?.map(f => {
                      return (
                        f.answer &&
                        f.answer !== '' && (
                          <ItemWrapper key={f.question}>
                            <LuxTypography variant="textMdRegular">
                              {f.question}
                            </LuxTypography>
                            <LuxTypography
                              variant="textMedium"
                              style={{
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {f.answer}
                            </LuxTypography>
                          </ItemWrapper>
                        )
                      );
                    })}
                </ColumnWrapper>
                {/* </PtDetailsWrapper> */}
              </ItemWrapper>
            );
          } else {
            return null;
          }
        })}
      </InsPtCtDetailsWrapper>
    </DetailSection>
  );
};
